/* ==========================================================================
   SPLASH Duel — the page's behaviour.
   Reads everything from SplashDuel (duel-data.js) and draws it. It never
   talks to the chain itself. No inline styles or scripts: the only styling
   it does is toggling classes and setting one custom property (--turn).
   ========================================================================== */
(function () {
  'use strict';

  var STILL_LOADING_MS = 8000;
  var MESSAGE_BYTES = 64;
  var ADDRESS = /^g1[0-9a-z]{38}$/;

  var body = document.body;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var encoder = new TextEncoder();

  function $(sel) { return document.querySelector(sel); }
  function $$(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }
  function setText(sel, text) { $$(sel).forEach(function (n) { n.textContent = text; }); }
  function make(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text !== undefined && text !== null) n.textContent = text;
    return n;
  }

  var el = {
    arena: $('[data-arena]'),
    coin: $('[data-coin]'),
    spin: $('[data-coin-spin]'),
    rings: $$('[data-ring]'),
    arenaTitle: $('[data-arena-title]'),
    arenaText: $('[data-arena-text]'),
    retry: $('[data-retry]'),
    result: $('[data-result]'),
    runBack: $('[data-run-back]'),
    back: $('[data-back]'),
    form: $('[data-putup]'),
    stakes: $$('.stake'),
    who: $('#duel-who'),
    whoError: $('[data-who-error]'),
    say: $('#duel-say'),
    sayError: $('[data-say-error]'),
    sayCount: $('[data-say-count]'),
    putUp: $('[data-put-up]'),
    putFine: $('[data-put-fine]'),
    table: $('[data-table]'),
    tableEmpty: $('[data-table-empty]'),
    air: $('[data-air]'),
    airEmpty: $('[data-air-empty]'),
    settled: $('[data-settled]'),
    settledEmpty: $('[data-settled-empty]'),
    flight: $('[data-flight]'),
    room: $('[data-room]'),
    roomList: $('[data-room-list]'),
    roomEmpty: $('[data-room-empty]'),
    roomForm: $('[data-room-form]'),
    roomInput: $('#room-text'),
    roomSend: $('[data-room-send]'),
    roomError: $('[data-room-error]'),
    rehearsal: $('[data-rehearsal]'),
    compatWarning: $('[data-compat-warning]'),
    wallet: $('[data-wallet]'),
    guest: $('[data-guest]'),
    foot: $('[data-foot]'),
    toast: $('[data-toast]'),
    announce: $('[data-announce]')
  };

  var state = null;
  var stake = 50;
  var putting = false;
  var busy = {};
  var flipping = false;
  var lastResult = null;
  var loadingTimer = 0;
  var toastTimer = 0;

  /* ---- Formatting ------------------------------------------------------------ */
  function gnot(n) { return Number(n).toLocaleString('en-IE', { maximumFractionDigits: 2 }); }
  function block(n) { return Math.round(n).toLocaleString('en-IE'); }
  var fmtRound = block; // same "integer with thousands separators" shape; named for what it formats here
  // "in 4:12" from an absolute unix second (decidedAt), ticked against THIS
  // tab's clock -- fine for a display that updates every second, even though
  // the authoritative due/not-due call above is the CHAIN's clock, not this
  // one's. null means no beacon answer has landed yet (rehearsal, or the
  // first live poll still in flight) -- said plainly, not guessed at.
  // mm:ss, bare, for the big flight clock. countdownText() below wraps the
  // same arithmetic in a sentence ("in 4:12") for the per-duel rows; this
  // one is the number on its own because it is the biggest thing on the page.
  function clock(secs) {
    secs = Math.max(0, Math.ceil(secs));
    return Math.floor(secs / 60) + ':' + String(secs % 60).padStart(2, '0');
  }

  function countdownText(decidedAt) {
    if (decidedAt == null) return 'waiting on the beacon';
    var remaining = Math.max(0, Math.round(decidedAt - Date.now() / 1000));
    var m = Math.floor(remaining / 60);
    var s = remaining % 60;
    return 'in ' + m + ':' + (s < 10 ? '0' : '') + s;
  }
  function isMe(addr) { return !!state && !!state.me && addr === state.me; }
  function who(addr) { return isMe(addr) ? 'You' : addr.slice(0, 6) + '…' + addr.slice(-4); }
  function payout(s) { return Math.round(s * 2 * (1 - state.rake.share) * 100) / 100; }
  function bytes(text) { return encoder.encode(text).length; }
  // Whether duel-data.js's compatibility guard (chain/compat.js) has refused
  // the configured realm. This is UI belt-and-braces only: putUp()/take()
  // already refuse to sign anything on their own when this is true, before
  // this flag ever disables a button.
  function isIncompatible(s) { return !!(s.compat && !s.compat.ok); }

  function toast(message) {
    el.toast.textContent = message;
    el.toast.classList.add('is-shown');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () { el.toast.classList.remove('is-shown'); }, 4000);
  }
  function announce(message) { el.announce.textContent = message; }
  function replay(node, cls) { node.classList.remove(cls); void node.offsetWidth; node.classList.add(cls); }

  /* ---- Top ---------------------------------------------------------------------- */
  function renderTop(s) {
    el.rehearsal.hidden = !s.rehearsal;
    setText('[data-rehearsal-text]', 'Pretend GNOT on a practice machine — same rules, faster blocks. ' + s.network + ' itself is at block ' + block(s.networkHeight) + '.');
    el.compatWarning.hidden = !isIncompatible(s);
    if (isIncompatible(s)) setText('[data-compat-text]', s.compat.message);
    var guest = s.rehearsal && !!s.me;
    el.guest.hidden = !guest;
    el.wallet.hidden = guest;
    renderWallet(s);
    setText('[data-block]', block(s.currentBlock));
  }

  /* ---- The coin's home ------------------------------------------------------------ */
  function setCaption(title, text) {
    el.arenaTitle.textContent = title;
    el.arenaText.textContent = text || '';
  }

  function restingCaption(s) {
    // `due`/`published` come from the beacon (duel-data.js's refreshBeacon),
    // never guessed here -- see that file's `air` comment for why all three
    // of decidedAt/due/published are null together rather than defaulted.
    var mine = s.air.filter(function (d) { return d.creator === s.me || d.taker === s.me; });
    var decided = mine.filter(function (d) { return d.due && d.published; })[0];
    var due = mine.filter(function (d) { return d.due && !d.published; })[0];
    var pending = mine.filter(function (d) { return !d.due; })[0];
    var myOffer = s.me && s.table.filter(function (o) { return o.by === s.me; })[0];
    if (decided) return setCaption('Your coin is in.', 'Round ' + fmtRound(decided.round) + ' is in — anyone can settle this duel now, including you.');
    if (due) return setCaption('Your coin is in.', "Round " + fmtRound(due.round) + "'s number exists, waiting for someone to put it on chain. Nothing is lost; it's only late.");
    if (pending) return setCaption('Coin in the air.', 'Decided by drand round ' + fmtRound(pending.round) + ' — ' + countdownText(pending.decidedAt) + '.');
    if (myOffer) return setCaption('Your ' + gnot(myOffer.stake) + ' GNOT is up.', 'Waiting for a taker.');
    // "Nothing in the air" IS ABOUT YOU, not about the table — this whole
    // function only ever looks at `mine`. That was fine when it was the only
    // thing on the page saying so, and became a flat contradiction the moment
    // the flight banner went in above it: "THE NEXT FLIP — 2 coins in the air"
    // sitting directly over "Nothing in the air." Caught by screenshotting the
    // page with two live duels on it, not by reading the diff.
    //
    // So when somebody else's coins are up, say that instead. It is both
    // truthful and the better invitation: a flip is about to happen and you
    // are not in it.
    if (s.air.length > 0) {
      return setCaption(
        s.air.length === 1 ? 'A coin is in the air.' : s.air.length + ' coins are in the air.',
        'None of them yours — take one off the table and you are in the next flip.');
    }
    setCaption('Nothing in the air.', 'Put a stake up, or take one off the table.');
  }

  function restArena() {
    el.arena.classList.remove('is-flipping', 'is-landed', 'shows-waves');
    el.coin.setAttribute('aria-label', 'The SPLASH coin, droplet side showing');
    el.result.hidden = true;
    lastResult = null;
    if (state) restingCaption(state);
  }

  /* ---- Put a stake up ------------------------------------------------------------ */
  function renderForm(s) {
    el.stakes.forEach(function (lab) {
      var input = lab.querySelector('input');
      lab.classList.toggle('is-on', Number(input.value) === stake);
      input.checked = Number(input.value) === stake;
    });
    var over = bytes(el.say.value) > MESSAGE_BYTES;
    el.sayCount.textContent = bytes(el.say.value) + ' / ' + MESSAGE_BYTES;
    el.sayCount.classList.toggle('is-over', over);
    el.sayError.hidden = !over;
    el.say.classList.toggle('is-invalid', over);
    el.putUp.disabled = putting || over || isIncompatible(s);
    el.putUp.textContent = putting ? 'Waiting for the block…' : 'Put it up — ' + gnot(stake) + ' GNOT';
    el.putFine.replaceChildren(
      document.createTextNode('Winner takes both stakes minus ' + Math.round(s.rake.share * 100) + '% — '),
      make('strong', '', "you'd win " + gnot(payout(stake)) + ' GNOT.'),
      document.createTextNode(' Every flip also puts both of you in this week\'s draw.' + (s.rehearsal ? ' Pretend GNOT — this is a rehearsal.' : ' Wallet at this step, not before.'))
    );
  }

  function checkWho() {
    var v = el.who.value.trim();
    var bad = v !== '' && (!ADDRESS.test(v) || isMe(v));
    el.whoError.textContent = isMe(v) ? "That's you — call out someone else, or leave it empty for anyone." : "That doesn't look like a gno address. It starts with g1 and is 40 characters long.";
    el.whoError.hidden = !bad;
    el.who.classList.toggle('is-invalid', bad);
    el.who.setAttribute('aria-invalid', bad ? 'true' : 'false');
    return !bad;
  }

  /* ---- The boards ------------------------------------------------------------------ */
  function actionButton(label, cls, onClick, id) {
    var b = make('button', 'btn-take' + (cls ? ' ' + cls : ''), busy[id] ? 'Waiting for the block…' : label);
    b.type = 'button';
    b.disabled = !!busy[id] || flipping;
    b.addEventListener('click', onClick);
    return b;
  }

  function offerItem(o, s) {
    var forYou = o.target && isMe(o.target);
    var yours = isMe(o.by);
    var forOther = o.target && !forYou && !yours;
    var li = make('li', 'item' + (forYou ? ' item--for-you' : ''));
    var meta = 'offer #' + o.id + ' · posted at block ' + block(o.postedAt);

    if (forYou) {
      var top = make('div', 'item__top');
      top.append(make('span', 'tag tag--for-you', 'For you'), make('span', 'item__meta', meta));
      var line = make('p', 'item__line');
      line.append(make('span', 'item__addr', who(o.by)), document.createTextNode(' is calling you out.'));
      li.append(top, line, make('p', 'item__big', gnot(o.stake) + ' GNOT'));
      if (o.message) li.appendChild(make('p', 'item__say', '“' + o.message + '”'));
      var act = make('div', 'item__actions');
      var answerBtn = actionButton('Answer it', 'btn-take--solid', function () { doTake(o); }, o.id);
      if (isIncompatible(s)) answerBtn.disabled = true;
      act.append(answerBtn, make('span', 'item__win', 'win ' + gnot(payout(o.stake)) + ' GNOT'));
      li.appendChild(act);
      return li;
    }

    var head = make('div', 'item__top');
    var l = make('p', 'item__line');
    if (yours) {
      l.append(document.createTextNode('Your '), make('strong', 'item__stake', gnot(o.stake) + ' GNOT'), document.createTextNode(' is up.'));
    } else {
      l.append(make('span', 'item__addr', who(o.by)), document.createTextNode(' has '), make('strong', 'item__stake', gnot(o.stake) + ' GNOT'), document.createTextNode(' up.'));
    }
    head.append(l, make('span', 'item__aside', o.target ? 'For ' + who(o.target) : 'Anyone'));
    li.appendChild(head);
    if (o.message) li.appendChild(make('p', 'item__say', '“' + o.message + '”'));
    li.appendChild(make('p', 'item__meta', meta));
    if (yours) {
      var t = make('p', 'item__note');
      t.append(make('span', 'tag tag--yours', 'Yours'), document.createTextNode(' Waiting for a taker.'));
      li.appendChild(t);
    } else if (forOther) {
      li.appendChild(make('p', 'item__note', 'Only ' + who(o.target) + ' can answer this one.'));
    } else {
      var a = make('div', 'item__actions');
      var takeBtn = actionButton('Take it', '', function () { doTake(o); }, o.id);
      if (isIncompatible(s)) takeBtn.disabled = true;
      a.append(takeBtn, make('span', 'item__win', 'win ' + gnot(payout(o.stake)) + ' GNOT'));
      li.appendChild(a);
    }
    return li;
  }

  function renderTable(s) {
    var rank = function (o) { return o.target && isMe(o.target) ? 0 : isMe(o.by) ? 1 : 2; };
    var list = s.table.slice().sort(function (x, y) { return rank(x) - rank(y) || x.stake - y.stake; });
    var frag = document.createDocumentFragment();
    list.forEach(function (o) { frag.appendChild(offerItem(o, s)); });
    el.table.replaceChildren(frag);
    el.tableEmpty.hidden = list.length > 0;
    setText('[data-table-count]', list.length ? list.length + ' waiting for a taker' : '');
  }

  // The duel settles itself from the beacon's randomness at the round
  // Accept pinned — nobody "opens" anything, there is no secret and no
  // deadline. d.due/d.published (duel-data.js's refreshBeacon, read from
  // the beacon's own CurrentRound/Published, not this tab's clock) decide
  // which of three true states this duel is in; d.due===null means no
  // beacon answer has landed yet (rehearsal, or the first live poll still
  // in flight), and that is said plainly rather than guessed at.
  function airItem(d, s) {
    var li = make('li', 'item');
    var tag, note, button = null;
    var line = make('p', 'item__line');
    if (d.due && d.published) {
      tag = make('span', 'tag tag--settle', 'Decided');
      note = 'Round ' + fmtRound(d.round) + ' is in. This duel settles itself — anyone, including you, can resolve it now.';
      button = actionButton('Resolve it', 'btn-take--settle', function () { doSettle(d); }, d.id);
    } else if (d.due) {
      tag = make('span', 'tag tag--waiting', 'Round due');
      note = "Decided by drand round " + fmtRound(d.round) + " — the number exists, waiting for someone to put it on chain. Nothing is lost; it's only late, and anyone can publish it.";
    } else if (d.due === false) {
      tag = make('span', 'tag tag--waiting', 'Waiting');
      note = 'Decided by drand round ' + fmtRound(d.round) + ' — ' + countdownText(d.decidedAt) + '. That round does not exist yet. Nobody can know it.';
    } else {
      tag = make('span', 'tag tag--waiting', 'Waiting');
      note = 'Decided by drand round ' + fmtRound(d.round) + '. Nobody steers this — it settles itself once that round comes in.';
    }
    var a = make('span', 'item__addr', who(d.creator));
    var b = make('span', 'item__addr', who(d.taker));
    if (isMe(d.creator)) line.append(document.createTextNode('You vs '), b);
    else if (isMe(d.taker)) line.append(document.createTextNode('You vs '), a);
    else line.append(a, document.createTextNode(' vs '), b);
    line.appendChild(document.createTextNode(' · ' + gnot(d.stake) + ' GNOT each'));
    li.append(tag, line, make('p', 'item__note', note));
    if (button) { var act = make('div', 'item__actions'); act.appendChild(button); li.appendChild(act); }
    return li;
  }

  function renderAir(s) {
    // Decided-and-published (actionable by anyone) first, then due-but-
    // unpublished, then still-pending soonest-first — the order a player
    // would actually want to act on these in.
    var rank = function (d) { return d.due && d.published ? 0 : d.due ? 1 : 2; };
    var list = s.air.slice().sort(function (x, y) { return rank(x) - rank(y) || (x.decidedAt ?? Infinity) - (y.decidedAt ?? Infinity); });
    var frag = document.createDocumentFragment();
    list.forEach(function (d) { frag.appendChild(airItem(d, s)); });
    el.air.replaceChildren(frag);
    el.airEmpty.hidden = list.length > 0;
  }

  function renderSettled(s) {
    var frag = document.createDocumentFragment();
    // creator/taker ARE winner/loser here (duel-data.js's `settled`, from
    // RecentResults) -- there is no face to show (the old 'droplet'/'waves'
    // split was a fixed placeholder, never a real read); `round` is the real
    // thing this record can say about how the flip was decided.
    s.settled.slice(0, 5).forEach(function (f) {
      var winner = f.creator, loser = f.taker;
      var youIn = isMe(f.creator) || isMe(f.taker);
      var won = youIn && isMe(winner);
      var li = make('li', 'item item--settled');
      var left = make('div');
      var line = make('p', 'item__line');
      if (youIn) {
        left.appendChild(make('span', 'tag ' + (won ? 'tag--won' : 'tag--lost'), won ? 'Won' : 'Lost'));
        if (won) line.append(document.createTextNode('You beat '), make('span', 'item__addr', who(loser)));
        else line.append(make('span', 'item__addr', who(winner)), document.createTextNode(' beat you'));
      } else {
        line.append(make('span', 'item__addr', who(winner)), document.createTextNode(' beat '), make('span', 'item__addr', who(loser)));
      }
      left.append(line, make('p', 'item__meta', 'round ' + (f.round == null ? '—' : fmtRound(f.round)) + ' · block ' + block(f.block)));
      var net = youIn
        ? make('span', 'item__net ' + (won ? 'item__net--won' : 'item__net--lost'), won ? '+' + gnot(payout(f.stake) - f.stake) : '−' + gnot(f.stake))
        : make('span', 'item__net', gnot(f.stake) + ' each');
      li.append(left, net);
      frag.appendChild(li);
    });
    el.settled.replaceChildren(frag);
    el.settledEmpty.hidden = s.settled.length > 0;
    // The ring the realm actually keeps (ResultRingSize(), read live) -- said
    // here so "Settled lately" never reads as a full history it is not:
    // anything older lives only in the chain's event log, which this page
    // does not read.
    setText('[data-settled-sub]', 'Last ' + (s.resultRingSize ?? 32) + ' — older settlements live only in the chain’s event log.');
  }

  // THE ROOM.
  //
  // EVERY MESSAGE IS SET WITH textContent, NEVER innerHTML. These are
  // arbitrary strings typed by strangers and stored forever on a public
  // chain; the realm escapes them for ITS markdown page, which does nothing
  // for this one. textContent is the whole defence and it is sufficient
  // precisely because it never parses.
  function renderRoom(s) {
    // Hidden entirely when there is no room to show -- rehearsal, or the
    // read failed. An empty room and an unreachable one look identical if
    // you render both as "nobody has said anything yet", and only one of
    // those is the room's fault.
    if (!s.chat) { el.room.hidden = true; return; }
    el.room.hidden = false;

    var frag = document.createDocumentFragment();
    s.chat.messages.forEach(function (m) {
      var li = make('li', 'room__msg' + (m.mine ? ' room__msg--mine' : ''));
      li.append(
        make('span', 'room__who', who(m.author)),
        make('span', 'room__text', m.text),       // textContent, via make()
        make('span', 'room__block', 'block ' + block(m.height))
      );
      frag.appendChild(li);
    });
    el.roomList.replaceChildren(frag);
    el.roomEmpty.hidden = s.chat.messages.length > 0;

    // "ever" rather than "shown", because the ring forgets and the number
    // that matters to a reader is how much they are NOT seeing.
    setText('[data-room-count]', s.chat.seen + (s.chat.seen === 1 ? ' message' : ' messages') + ' ever');

    var canSay = !!s.wallet && !s.rehearsal;
    el.roomSend.disabled = !canSay;
    el.roomInput.disabled = !canSay;
    el.roomInput.maxLength = s.chat.maxLen || 280;
    setText('[data-room-fine]', canSay ? '' : 'Connect a wallet to say something.');
  }

  // Posting. Errors are shown in the form, not as a toast: the player is
  // looking at the input they just typed into, and a message that vanishes
  // after three seconds is the wrong place for "that is 284 bytes".
  if (el.roomForm) {
    el.roomForm.addEventListener('submit', function (ev) {
      ev.preventDefault();
      var text = el.roomInput.value;
      el.roomError.hidden = true;
      el.roomSend.disabled = true;
      Promise.resolve(window.SplashDuel.say(text)).then(function () {
        el.roomInput.value = '';
        updateRoomCount();
      }).catch(function (e) {
        el.roomError.textContent = e && e.message ? e.message : String(e);
        el.roomError.hidden = false;
      }).then(function () {
        el.roomSend.disabled = false;
      });
    });
    el.roomInput.addEventListener('input', updateRoomCount);
  }

  // BYTES, NOT CHARACTERS, and the same count the contract makes. The realm
  // measures len(text) in bytes because bytes are what storage costs, so a
  // message of emoji is far longer than it looks. A counter showing
  // characters would read "40 / 280" on something the chain refuses.
  function updateRoomCount() {
    if (!el.roomInput) return;
    var max = (state && state.chat && state.chat.maxLen) || 280;
    var n = new TextEncoder().encode(el.roomInput.value).length;
    setText('[data-room-count-chars]', n + ' / ' + max);
  }

  // THE NEXT FLIP, as one event rather than a crowd of private countdowns.
  //
  // The countdown is driven off decidedAt, which came from the BEACON's own
  // TimeOfRound -- not recomputed here. When the round is late (its number
  // exists, nobody has relayed it) it says so as a status rather than a
  // failure, same as the per-duel caption: nothing is lost and anyone can
  // push it through.
  function renderFlight(s) {
    var f = s.flights && s.flights[0];
    // THE JOIN LINE SHOWS EVEN WITH AN EMPTY TABLE, which is the whole point:
    // "get your challenges in" has to be visible BEFORE there is anything in
    // the air, or it only ever tells people about a party they already see.
    var j = s.join;
    if (!f && !j) { el.flight.hidden = true; return; }
    el.flight.hidden = false;
    if (!f) {
      // Nothing in the air yet, but there IS a flip forming. Say so.
      el.flight.classList.remove('is-mine');
      setText('[data-flight-clock]', '—');
      setText('[data-flight-what]', 'Nothing in the air yet. Be the first coin in the next flip.');
      $('[data-flight-after]').hidden = true;
      renderJoinLine(s);
      return;
    }
    el.flight.classList.toggle('is-mine', !!f.mine);

    var left = f.decidedAt == null ? null : Math.round(f.decidedAt - Date.now() / 1000);
    if (left == null) {
      setText('[data-flight-clock]', 'soon');
    } else if (left > 0) {
      setText('[data-flight-clock]', clock(left));
    } else {
      setText('[data-flight-clock]', 'any moment');
    }

    var coins = f.count === 1 ? '1 coin' : f.count + ' coins';
    var line = coins + ' · ' + gnot(f.staked) + ' GNOT in the air';
    if (left != null && left <= 0) {
      line += ' — round ' + block(f.round) + ' has landed, settling now';
    } else {
      line += ' — all on drand round ' + block(f.round) + ', together';
    }
    if (f.mine) line += '. You are in this one.';
    setText('[data-flight-what]', line);

    renderJoinLine(s);

    // What is forming behind it, so the page reads as a rhythm rather than
    // one lonely timer.
    var rest = (s.flights || []).slice(1);
    var after = $('[data-flight-after]');
    if (rest.length === 0) { after.hidden = true; } else {
      after.hidden = false;
      var n = rest.reduce(function (t, x) { return t + x.count; }, 0);
      setText('[data-flight-after]', 'Then ' + (n === 1 ? '1 more coin' : n + ' more coins')
        + ' on ' + (rest.length === 1 ? 'the next round' : rest.length + ' later rounds') + '.');
    }
  }

  // "Take one in the next 2:14 and you are in the 21:47 flip."
  //
  // Hidden whenever duel-data could not CONFIRM the deadline against the
  // realm's own RoundForAcceptAt -- a made-up deadline about somebody's money
  // is worse than no deadline.
  function renderJoinLine(s) {
    var el2 = $('[data-flight-join]');
    if (!el2) return;
    var j = s.join;
    if (!j) { el2.hidden = true; return; }
    var left = Math.round(j.closesAt - Date.now() / 1000);
    if (left <= 0) { el2.hidden = true; return; }   // mid-rollover; next poll fixes it
    el2.hidden = false;
    el2.textContent = 'Take a stake in the next ' + clock(left)
      + ' and you are in the ' + hhmm(j.flipAt) + ' flip.';
  }

  function hhmm(unix) {
    var d = new Date(unix * 1000);
    return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
  }

  function renderHow(s) {
    var machine = s.table.reduce(function (t, o) { return t + o.stake; }, 0) + s.air.reduce(function (t, d) { return t + d.stake * 2; }, 0);
    setText('[data-rake-pct]', Math.round(s.rake.share * 100) + '%');
    setText('[data-rake-draw]', Math.round(s.rake.toDraw * 100) + '%');
    setText('[data-machine]', gnot(machine) + ' GNOT');
  }

  // THE REALM LINE CARRIES .fair__realm FOR ONE REASON: that class is the hook on
  // splash.css's word-break rule, and WITHOUT IT THIS LINE OVERFLOWED EVERY PHONE.
  // A package path under an address namespace is 40 characters of unbroken base32
  // -- measured at 465px inside a 390px viewport, which made duel.html 662px wide
  // and the single worst offender on the site.
  //
  // THE RULE ALREADY EXISTED AND ALREADY SAID WHY. It was written after the same
  // overflow was traced on the homepage, and its own comment records that the
  // investigation first blamed a nav tab and was wrong. It targets .fair__realm
  // and [data-realm-line]; index.html and hourly.html were given those hooks and
  // THIS ELEMENT WAS NOT, so the fix sat one class away from the page that needed
  // it most. A fix wired to nothing is indistinguishable from no fix.
  function renderFoot(s) {
    var inner = make('div', 'foot__inner');
    inner.append(make('span', 'fair__realm', 'Realm ' + s.realm), make('span', '', 'Stakes in wrapped GNOT' + (s.rehearsal ? ' · rehearsal build' : '')));
    el.foot.replaceChildren(inner);
  }

  function render(next) {
    state = next;
    window.clearTimeout(loadingTimer);
    body.classList.remove('is-loading', 'is-offline');
    el.retry.hidden = true;
    renderTop(next);
    renderForm(next);
    renderTable(next);
    renderAir(next);
    renderSettled(next);
    renderFlight(next);
    renderRoom(next);
    renderHow(next);
    renderFoot(next);
    if (!flipping && !lastResult) restingCaption(next);
  }

  /* ---- The flip: tumble → land → ripple → reveal ----------------------------------- */
  function showResult(r) {
    var faceWord = r.face === 'droplet' ? 'Droplet' : 'Waves';
    el.arena.classList.remove('is-flipping');
    el.arena.classList.add('is-landed');
    el.arena.classList.toggle('shows-waves', r.face === 'waves');
    el.coin.setAttribute('aria-label', 'The coin landed ' + r.face + ' side up');
    if (!reduceMotion.matches) el.rings.forEach(function (ring) { replay(ring, 'is-rippling'); });
    setCaption(faceWord + ' side up', 'You had the ' + r.youHad + (r.youWon ? ' — you won.' : ' — ' + who(r.opponent) + ' took it.'));

    el.result.classList.toggle('is-lost', !r.youWon);
    setText('[data-result-label]', r.youWon ? 'You won' : who(r.opponent) + ' took it');
    var amount = $('[data-result-amount]');
    amount.replaceChildren(document.createTextNode(r.youWon ? gnot(r.payout) + ' ' : '−' + gnot(r.stake) + ' '), make('small', '', 'GNOT'));
    setText('[data-result-text]', r.youWon
      ? 'Both stakes (' + gnot(r.stake * 2) + ' GNOT) minus the ' + gnot(r.rakeToDraw + r.rakeToCosts) + ' GNOT rake. That\'s ' + gnot(r.payout - r.stake) + ' more than you put in.'
      : 'Your stake went to ' + who(r.opponent) + '. The flip still added ' + gnot(r.rakeToDraw) + ' GNOT to this week\'s draw, and you both got entries for it.');
    setText('[data-result-draw]', gnot(r.rakeToDraw) + ' GNOT');
    setText('[data-result-costs]', gnot(r.rakeToCosts) + ' GNOT');
    setText('[data-result-block]', 'block ' + block(r.block) + ' · settled by the realm itself');
    el.runBack.textContent = 'Run it back — ' + gnot(r.stake) + ' GNOT';
    el.result.hidden = false;
    flipping = false;
    lastResult = r;
    if (state) { renderTable(state); renderAir(state); }
    announce(faceWord + ' side up. ' + (r.youWon ? 'You won ' + gnot(r.payout) + ' GNOT.' : who(r.opponent) + ' won.'));
    el.result.scrollIntoView({ block: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }

  function onFlip(r) {
    if (!r.youIn) { toast('A coin landed ' + r.face + ' side up on the table.'); return; }
    el.result.hidden = true;
    if (reduceMotion.matches) { showResult(r); return; }
    flipping = true;
    el.arena.classList.remove('is-landed', 'shows-waves');
    el.spin.style.setProperty('--turn', r.face === 'droplet' ? '1800deg' : '1980deg');
    setCaption('Flipping…', 'The round is in. Reading the number.');
    void el.spin.offsetWidth;
    el.arena.classList.add('is-flipping');
    var done = false;
    var finish = function () { if (done) return; done = true; showResult(r); };
    el.spin.addEventListener('animationend', finish, { once: true });
    window.setTimeout(finish, 2600);                                     // in case the animation never reports back
  }

  /* ---- Actions ------------------------------------------------------------------------ */
  function doTake(o) {
    if (busy[o.id]) return;
    busy[o.id] = true;
    renderTable(state);
    window.SplashDuel.take(o.id).then(function () {
      toast(o.target ? 'Answered — the coin opens once ' + who(o.by) + ' settles.' : "You're in — the coin opens once " + who(o.by) + ' settles.');
      if (!lastResult) restingCaption(state);
      $('[data-arena]').scrollIntoView({ block: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }).catch(function (err) { toast(err.message); }).then(function () {
      delete busy[o.id];
      if (state) renderTable(state);
    });
  }

  function doSettle(d) {
    if (busy[d.id]) return;
    busy[d.id] = true;
    renderAir(state);
    window.SplashDuel.settle(d.id).catch(function (err) { toast(err.message); }).then(function () {
      delete busy[d.id];
      if (state) renderAir(state);
    });
  }

  // doFinishReveal: the taker's retry for RevealNonce. Reachable from a duel
  // whose Accept already landed but whose nonce did not go through yet —
  // whether that is take()'s own first attempt failing, or a pending reveal
  // this page found on a fresh load (duel-data.js's nonceStatusFor runs on
  // every refresh, not just right after take()). Same shape as doSettle on
  // purpose: one busy flag per duel id, one toast on failure, re-render either
  // way.
  function doFinishReveal(d) {
    if (busy[d.id]) return;
    busy[d.id] = true;
    renderAir(state);
    window.SplashDuel.finishRevealNonce(d.id).then(function () {
      toast('Revealed — the coin opens once ' + who(d.creator) + ' settles.');
    }).catch(function (err) { toast(err.message); }).then(function () {
      delete busy[d.id];
      if (state) renderAir(state);
    });
  }

  function doPutUp(target) {
    if (putting) return;
    var message = el.say.value.trim();
    if (bytes(message) > MESSAGE_BYTES) return;
    putting = true;
    renderForm(state);
    window.SplashDuel.putUp(stake, target || null, message).then(function (offer) {
      toast('Your ' + gnot(offer.stake) + ' GNOT is on the table' + (offer.target ? ' for ' + who(offer.target) + '.' : '.'));
      announce('Your stake is on the table.');
      el.who.value = '';
      el.say.value = '';
      if (!lastResult) restingCaption(state);
    }).catch(function (err) { toast(err.message); }).then(function () {
      putting = false;
      renderForm(state);
    });
  }

  el.stakes.forEach(function (lab) {
    lab.querySelector('input').addEventListener('change', function (e) {
      stake = Number(e.target.value);
      if (state) renderForm(state);
    });
  });
  el.say.addEventListener('input', function () { if (state) renderForm(state); });
  el.who.addEventListener('blur', checkWho);
  el.who.addEventListener('input', function () { if (!el.whoError.hidden) checkWho(); });
  el.form.addEventListener('submit', function (e) {
    e.preventDefault();
    if (!state || !checkWho()) { if (state) el.who.focus(); return; }
    doPutUp(el.who.value.trim());
  });

  el.runBack.addEventListener('click', function () {
    var r = lastResult;
    if (!r) return;
    stake = r.stake;
    restArena();
    renderForm(state);
    doPutUp(r.opponent);                                               // same stake, called out to the same person
  });
  el.back.addEventListener('click', function () {
    restArena();
    $('[data-table]').closest('.board').scrollIntoView({ block: 'start', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  });


  // THE WALLET PILL, one implementation written three times on purpose.
  //
  // It carries its OWN shortener rather than calling the file's. Earlier today
  // this exact block was copied between two views whose shorteners are named
  // short() and who() -- the second threw ReferenceError on every render,
  // silently, and the pill simply never updated. And the duel's who() answers
  // "You" for your own address, which is right in a list of players and wrong on
  // a pill whose whole job is to show WHICH address. Self-contained is cheaper
  // than correct-by-coincidence.
  function renderWallet(s) {
    var a = s && s.wallet && s.wallet.address;
    if (a) {
      el.wallet.textContent = a.slice(0, 6) + '\u2026' + a.slice(-4);
      el.wallet.title = a + ' \u2014 click to disconnect';
      el.wallet.classList.add('is-connected');
      el.wallet.disabled = false;
    } else {
      el.wallet.textContent = 'Connect wallet';
      el.wallet.removeAttribute('title');
      el.wallet.classList.remove('is-connected');
      el.wallet.disabled = false;
    }
  }

  el.wallet.addEventListener('click', function () {
    var D = window.SplashDuel;
    if (el.wallet.classList.contains('is-connected')) {
      D.disconnect().then(function (res) { toast(res.message); }).catch(function (err) { toast(err.message); });
      return;
    }
    D.connectWallet().then(function (res) { toast(res.message); }).catch(function (err) { toast(err.message); });
  });

  /* ---- Loading, errors, notices ------------------------------------------------------ */
  function showError(message) {
    window.clearTimeout(loadingTimer);
    body.classList.remove('is-loading');
    if (state) { toast(message); return; }
    body.classList.add('is-offline');
    setCaption("Can't reach the chain.", message);
    el.retry.hidden = false;
  }

  function startLoading() {
    body.classList.add('is-loading');
    body.classList.remove('is-offline');
    el.retry.hidden = true;
    setCaption('Reading the realm…', '');
    loadingTimer = window.setTimeout(function () { setCaption('Still reading the realm…', 'The chain may be slow.'); }, STILL_LOADING_MS);
    window.SplashDuel.start();
  }
  el.retry.addEventListener('click', startLoading);

  window.SplashDuel.onChange(function (evt) {
    if (evt.type === 'state') render(evt.state);
    else if (evt.type === 'flip') onFlip(evt.result);
    else if (evt.type === 'notice') { toast(evt.message); announce(evt.message); }
    else if (evt.type === 'error') showError(evt.message);
  });

  window.addEventListener('hashchange', function () {
    if (!/^#demo-[a-z]+$/.test(window.location.hash)) return;
    state = null;
    flipping = false;
    busy = {};
    putting = false;
    restArena();
    window.scrollTo(0, 0);
    startLoading();
  });

  startLoading();
})();
